/**
 * BauxEnAttenteTile — Bloc Rose "Bail en attente".
 *
 * Affiche les baux dont la signature traîne depuis trop longtemps.
 * Délai de référence : > 7 jours sans signature complète.
 *
 * Actions :
 *  - "Relancer" → renvoie un email aux signataires manquants
 *  - "Terminer" → archive le bail (envoi au Bloc Doré, suppression J+15)
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Loader2, Clock, AlertTriangle, Archive, Copy, ExternalLink } from "lucide-react";
import { toast } from "@/lib/toast";
import { API, authH } from "@/api";


const DAYS_PENDING_THRESHOLD = 7;

export default function BauxEnAttenteTile({ masterId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionBusy, setActionBusy] = useState(null);

  const load = useCallback(async () => {
    if (!masterId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/immeuble/${masterId}/zone1/leases`, { headers: authH() });
      const all = r.data?.items || [];
      // Filtre : seulement baux pending depuis plus de X jours
      const now = Date.now();
      const slow = all.filter((l) => {
        if (l.status === "completed" || l.status === "active") return false;
        if (!l.created_at) return false;
        const age = (now - new Date(l.created_at).getTime()) / 86400000;
        return age >= DAYS_PENDING_THRESHOLD;
      });
      setItems(slow);
    } catch {
      toast.error("Impossible de charger les baux en attente");
    } finally {
      setLoading(false);
    }
  }, [masterId]);

  useEffect(() => { load(); }, [load]);

  const archiveLease = async (lease) => {
    if (!window.confirm(
      `Archiver ce bail ?\n\n` +
      `• Il disparaîtra de la liste\n` +
      `• Il sera supprimé définitivement dans 15 jours (visible dans le module Archives)\n\n` +
      `Confirmer ?`
    )) return;
    setActionBusy(lease.id);
    try {
      await axios.post(`${API}/admin/immeuble/archives/add`, {
        entity_type: "lease",
        entity_id: lease.id,
        master_id: masterId,
        label: `Bail ${lease.unit_label || ""} — ${lease.signers?.map(s => s.name).join(", ") || "Inconnu"}`,
        snapshot: lease,
      }, { headers: authH() });
      toast.success("Bail archivé — sera supprimé dans 15 jours");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'archivage");
    } finally {
      setActionBusy(null);
    }
  };

  const copyLink = (token) => {
    const url = `${window.location.origin}/sign/${token}`;
    navigator.clipboard.writeText(url);
    toast.success("Lien copié");
  };

  if (loading) {
    return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  }

  return (
    <div className="bg-white border-2 border-rose-200 rounded-xl p-5" data-testid="baux-en-attente-tile">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-rose-100 text-rose-700 flex items-center justify-center">
          <Clock className="w-5 h-5" />
        </div>
        <div>
          <h2 className="text-base font-bold text-slate-900">Bail en attente</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Baux non signés depuis plus de {DAYS_PENDING_THRESHOLD} jours. Une action manuelle peut être requise.
          </p>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-8 text-sm text-slate-400">
          <AlertTriangle className="w-8 h-8 mx-auto mb-2 opacity-40" />
          Aucun bail en attente depuis plus de {DAYS_PENDING_THRESHOLD} jours.
        </div>
      ) : (
        <ul className="space-y-3">
          {items.map((l) => {
            const days = Math.floor((Date.now() - new Date(l.created_at).getTime()) / 86400000);
            const pendingSigners = (l.signers || []).filter(s => !s.signed_at);
            return (
              <li key={l.id} className="border border-rose-100 bg-rose-50/30 rounded-lg p-3" data-testid={`pending-lease-${l.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-800 text-sm">
                      Bail {l.unit_label || l.unit_id?.slice(0, 6)}
                    </div>
                    <div className="text-[10px] text-rose-700 mt-0.5 font-semibold">
                      ⏱ {days} jours sans signature complète
                    </div>
                    {pendingSigners.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {pendingSigners.map((s) => (
                          <div key={s.token || s.email} className="text-[11px] flex items-center gap-2 flex-wrap">
                            <span className="text-slate-700">⏳ {s.name}</span>
                            <span className="text-slate-400">({s.email})</span>
                            {s.token && (
                              <button onClick={() => copyLink(s.token)}
                                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-white border text-[10px] text-slate-600 hover:bg-slate-50">
                                <Copy className="w-2.5 h-2.5" /> Lien
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1.5">
                    {l.drive_url && (
                      <a href={l.drive_url} target="_blank" rel="noreferrer"
                        className="px-2 py-1 rounded bg-white border border-slate-300 hover:bg-slate-50 text-[10px] inline-flex items-center gap-1">
                        <ExternalLink className="w-3 h-3" /> Drive
                      </a>
                    )}
                    <button
                      onClick={() => archiveLease(l)}
                      disabled={actionBusy === l.id}
                      className="px-2 py-1 rounded bg-yellow-600 hover:bg-yellow-700 text-white text-[10px] font-semibold inline-flex items-center gap-1 disabled:opacity-50"
                      title="Terminer — envoie aux archives (suppression J+15)"
                      data-testid={`pending-lease-archive-${l.id}`}
                    >
                      <Archive className="w-3 h-3" /> Terminer
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
